import { CardContent, Grid, Pagination, Typography } from "@mui/material";
import { MprcCard, MprcContainer, MprcIconButtonDropdown, MprcInfiniteLoading, MprcRequestErrorReport } from "components";
import { MprcFilterCountInfo } from "components/filter";
import { MprcCardFilterBox } from "components/filter/MprcCardFilterBox";
import { MprcFilterEmptyDataset } from "components/filter/MprcFilterEmptyDataset";
import { IconButtonBack } from "components/iconButtons";
import { IconSort } from "components/icons";
import { ChannelSearchDtoPaginatedList, ChannelSearchOrderBy } from "generated/CoreApi_v1";
import useDidUpdateEffect from "hooks/useNoInitialEffect";
import { useCoreApi } from "providers/CoreApiProvider";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router";
import { useGobackOrHome } from "views/common/hooks/useGobackOrHome";
import { GlobalSearchRowsPerPage } from "views/general/GlobalSearch/components/GlobalSearchTabs";
import { SearchRenderChannel } from "views/general/GlobalSearch/components/SearchRenderChannel";

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export const FullChannelSearch = () => {
    const [lastError, setLastError] = useState<any>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [orderBy, setOrderBy] = useState<ChannelSearchOrderBy>(ChannelSearchOrderBy.Relevance);
    const [globalSearchText, setGlobalSearchText] = useState<string>("");

    const channelResult = useRef<ChannelSearchDtoPaginatedList>(null!);

    const { t } = useTranslation("shared");
    const [searchParams] = useSearchParams();
    const api = useCoreApi();
    const navigate = useNavigate();
    const goback = useGobackOrHome();

    ///
    const getState = () => {
        return { pageNumber, orderBy, globalSearchText };
    };

    ///
    async function loadChannels(pageNumber: number) {
        setLastError(null);

        try {
            const response = await api.v1.globalSearch.channelSearch({ searchText: globalSearchText, orderBy, pageNumber, pageSize: GlobalSearchRowsPerPage });
            channelResult.current = response.data;
        }
        catch (e) {
            setLastError(e);
        }
        finally {
            setLoading(false);
        }
    }

    ///
    const renderSearch = () => {

        if (loading) return null;

        return channelResult.current.totalRows == 0
            ? <MprcFilterEmptyDataset message={t("GlobalSearch.FullSearch.NoChannelsFound")} />
            : (
                <>
                    <MprcContainer>
                        <MprcFilterCountInfo
                            text={t("GlobalSearch.FullSearch.ChannelsFound", { count: channelResult.current.totalRows })}
                        />
                    </MprcContainer>

                    {
                        channelResult.current.items.map((v, index) => {
                            return <SearchRenderChannel
                                key={index}
                                data={v}
                                onBeforeLinkClick={() => navigate(document.location.search, { replace: true, state: { state: getState() } })}
                            />;
                        })
                    }

                    {
                        channelResult.current.totalPages > 1 &&
                        <Pagination
                            shape="rounded"
                            color="primary"
                            page={channelResult.current.pageNumber}
                            count={channelResult.current.totalPages}
                            onChange={(e, pn) => setPageNumber(pn)}
                            sx={{ "& ul": { justifyContent: "center" } }}
                        />
                    }
                </>
            );
    };

    /////////////////////////////////////////////////////////////////////////////////////////////////

    useEffect(() => {
        const q = searchParams.get("q");
        setGlobalSearchText(q == null ? "" : decodeURIComponent(q));
        setPageNumber(1);
        setOrderBy(ChannelSearchOrderBy.Relevance);
    }, [document.location.search]);

    useDidUpdateEffect(() => {
        loadChannels(pageNumber);
    }, [orderBy, pageNumber, globalSearchText]);

    /////////////////////////////////////////////////////////////////////////////////////////////////

    return (
        <div>
            <MprcInfiniteLoading visible={loading} />

            {
                lastError
                    ? <MprcRequestErrorReport error={lastError} />
                    : (
                        <MprcContainer>
                            <MprcCard>
                                <MprcCardFilterBox>
                                    <Grid container justifyContent="space-between" alignItems="center">
                                        <Grid>
                                            <Grid container spacing={1} alignItems="center">
                                                <Grid>
                                                    <IconButtonBack onClick={goback} />
                                                </Grid>
                                                <Grid>
                                                    <Typography variant="h4">{t("GlobalSearch.FullSearch.ChannelTitle")}</Typography>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        <Grid>
                                            <MprcIconButtonDropdown
                                                icon={<IconSort />}
                                                title={t("shared:Filter.OrderBy")}
                                                onChange={(orderBy) => setOrderBy(orderBy)}
                                                selected={orderBy}
                                                menuItemCheck
                                                items={
                                                    [
                                                        { text: t("GlobalSearch.Filters.ChannelOrderBy.Relevance"), value: ChannelSearchOrderBy.Relevance },
                                                        { text: t("GlobalSearch.Filters.ChannelOrderBy.Publications"), value: ChannelSearchOrderBy.Publications },
                                                        { text: t("GlobalSearch.Filters.ChannelOrderBy.Name"), value: ChannelSearchOrderBy.Name },
                                                    ]
                                                }
                                            />
                                        </Grid>
                                    </Grid>
                                </MprcCardFilterBox>

                                <CardContent>
                                    {renderSearch()}
                                </CardContent>

                            </MprcCard>
                        </MprcContainer>
                    )
            }
        </div>
    );
};
